import {Languages, LanguageService, Office, useService} from '@shared/core';
import {useTranslation} from 'react-i18next';

const regexForAddress = {
  ru: /^[а-яА-ЯёЁ0-9\s.,№\-/]{5,100}$/,
  en: /^[a-zA-Z0-9\s.,№\-/]{5,100}$/,
};

const regexForCity = {
  ru: /^[А-Яа-яЁё\s-]{2,50}$/,
  en: /^[A-Za-z\s-]{2,50}$/,
};

const getAddressLettersType = (lang: string, inputType: string) => {
  const letterType = {
    ru: {
      latin: 'латиницу',
      cyrillic: 'кириллицу',
    },
    en: {
      latin: 'latin',
      cyrillic: 'cyrillic',
    },
  };
  return letterType?.[lang]?.[inputType];
};

export const useValidationRules = (offices?: Office[], jiraOfficeId?: string) => {
  const {t} = useTranslation();
  const {getAppLanguage} = useService(LanguageService);
  const language = getAppLanguage() ?? Languages.Ru;

  const validateNoMultipleSpaces = (value: string) => {
    if (!value) return true;

    const trimmedValue = value.trim();

    if (trimmedValue.length === 0) {
      return t('modals.messages.noOnlySpaces');
    }

    if (/\s{2,}/.test(value)) {
      return t('modals.messages.noMultipleSpaces');
    }

    return true;
  };

  const commonAddressRules = {
    required: {value: true, message: t('modals.messages.required')},
    minLength: {value: 5, message: t('modals.messages.lengthLimit', {min: 5, max: 100})},
    maxLength: {value: 100, message: t('modals.messages.lengthLimit', {min: 5, max: 100})},
    validate: {
      noMultipleSpaces: validateNoMultipleSpaces,
    },
  };

  const commonCityRules = {
    required: {value: true, message: t('modals.messages.required')},
    minLength: {value: 2, message: t('modals.messages.lengthLimit', {min: 2, max: 50})},
    maxLength: {value: 50, message: t('modals.messages.lengthLimit', {min: 2, max: 50})},
    validate: {
      noMultipleSpaces: validateNoMultipleSpaces,
    },
  };

  const cityRulesRu = {
    ...commonCityRules,
    pattern: {
      message: t('modals.messages.city', {lang: getAddressLettersType(language, 'cyrillic')}),
      value: regexForCity.ru,
    },
  };

  const cityRulesEn = {
    ...commonCityRules,
    pattern: {
      message: t('modals.messages.city', {lang: getAddressLettersType(language, 'latin')}),
      value: regexForCity.en,
    },
  };

  const jiraOfficeIdRules = {
    required: {value: true, message: t('modals.messages.required')},
    validate: {
      uniqueId: (value: string) => {
        if (!value) return true;
        const existingOffice = offices?.find(
          (office) => office.jiraOfficeId === value && office.jiraOfficeId !== jiraOfficeId,
        );
        return !existingOffice || t('modals.messages.jiraOfficeIdExists');
      },
    },
  };

  return {
    addressRulesRu: {
      ...commonAddressRules,
      pattern: {
        value: regexForAddress.ru,
        message: t('modals.messages.address', {lang: getAddressLettersType(language, 'cyrillic')}),
      },
    },
    addressRulesEn: {
      ...commonAddressRules,
      pattern: {
        value: regexForAddress.en,
        message: t('modals.messages.address', {lang: getAddressLettersType(language, 'latin')}),
      },
    },
    cityRulesRu,
    cityRulesEn,
    jiraOfficeIdRules,
  };
};
